import AddIcon from '@mui/icons-material/Add'
import DownloadIcon from '@mui/icons-material/Download'
import {
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  LinearProgress,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { Dialog } from '@/components/AppDialog'
import BlockIcon from '@mui/icons-material/Block'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import EditIcon from '@mui/icons-material/Edit'
import { useEffect, useState } from 'react'
import { api, ApiError } from '@/api/client'
import { useCustomer, useCustomers, useGstinLookup, useSaveCustomer, useSetCustomerEnabled } from '@/api/hooks'
import type { CustomerDetail } from '@/api/types'
import { useAuthStore } from '@/auth/authStore'
import {
  EmptyState,
  ErrorBanner,
  formatMobile,
  formatMoney,
  formatNumber,
  humanise,
  PageHeader,
} from '@/components/common'
import KeyIcon from '@mui/icons-material/VpnKey'
import HistoryIcon from '@mui/icons-material/History'
import { PortalAccessDialog } from '@/pages/customers/PortalAccessDialog'
import { CustomerHistoryDialog } from '@/pages/customers/CustomerHistoryDialog'

/**
 * A person or a firm, and nothing in between.
 *
 * <p>Institution and event planner were both firms as far as anything in the
 * system did with them - same billing, same GSTIN, same M/s. - so they were
 * three ways of saying company and a decision the office had to make on every
 * customer for no gain.</p>
 */
const CUSTOMER_TYPES = ['INDIVIDUAL', 'COMPANY']

export function CustomersPage() {
  const can = useAuthStore((state) => state.can)
  const canWrite = useAuthStore((state) => state.canWrite)
  const [q, setQ] = useState('')
  const [type, setType] = useState('')
  const [page, setPage] = useState(0)
  const [size, setSize] = useState(20)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editing, setEditing] = useState<string | null>(null)
  const [portalFor, setPortalFor] = useState<{ id: string; name: string } | null>(null)
  const [historyFor, setHistoryFor] = useState<{ id: string; name: string } | null>(null)
  const setEnabled = useSetCustomerEnabled()

  const { data, isLoading, error } = useCustomers({
    q: q || undefined,
    type: type || undefined,
    page,
    size,
  })

  return (
    <>
      <PageHeader
        title="Customers"
        subtitle="Individuals, corporates, institutions and event planners"
        actions={
          <Stack direction="row" spacing={1}>
            {can('customer.export') && (
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={() => api.download('/api/v1/reports/CUSTOMER_HISTORY/export', {}, 'customers.xlsx')}
              >
                Export
              </Button>
            )}
            {canWrite('customer') && (
              <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialogOpen(true)}>
                New customer
              </Button>
            )}
          </Stack>
        }
      />

      <ErrorBanner error={error} />

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <TextField
              label="Search name, mobile, email or code"
              value={q}
              onChange={(event) => {
                setQ(event.target.value)
                setPage(0)
              }}
              sx={{ minWidth: 320 }}
            />
            <TextField
              select
              label="Type"
              value={type}
              onChange={(event) => {
                setType(event.target.value)
                setPage(0)
              }}
              sx={{ minWidth: 200 }}
            >
              <MenuItem value="">All types</MenuItem>
              {CUSTOMER_TYPES.map((value) => (
                <MenuItem key={value} value={value}>
                  {humanise(value)}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
        </CardContent>
      </Card>

      <Card>
        {isLoading && <LinearProgress />}
        {data && data.content.length === 0 ? (
          <EmptyState message="No customers match this search." />
        ) : (
          <>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Code</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Mobile</TableCell>
                  <TableCell>City</TableCell>
                  <TableCell align="right">Enquiries</TableCell>
                  <TableCell align="right">Orders</TableCell>
                  <TableCell align="right">Lifetime sales</TableCell>
                  <TableCell align="right">Outstanding</TableCell>
                  <TableCell align="right">Rating</TableCell>
                  <TableCell align="right" />
                </TableRow>
              </TableHead>
              <TableBody>
                {data?.content.map((customer) => (
                  <TableRow key={customer.id} hover>
                    <TableCell>{customer.customerCode}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.75} alignItems="center">
                        <Typography variant="body2">
                          {customer.title ? `${customer.title} ` : ''}
                          {customer.name}
                        </Typography>
                        {!customer.enabled && (
                          <Chip label="Disabled" size="small" color="default" variant="outlined" />
                        )}
                      </Stack>
                      {customer.companyName && (
                        <Typography variant="caption" color="text.secondary">
                          {customer.companyName}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{humanise(customer.customerType)}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatMobile(customer.mobile)}</TableCell>
                    <TableCell>{customer.billingCity ?? '—'}</TableCell>
                    <TableCell align="right">{formatNumber(customer.totalEnquiries)}</TableCell>
                    <TableCell align="right">{formatNumber(customer.confirmedOrders)}</TableCell>
                    <TableCell align="right">{formatMoney(customer.lifetimeSales)}</TableCell>
                    <TableCell align="right">
                      <Typography
                        variant="body2"
                        color={customer.outstandingBalance > 0 ? 'warning.main' : 'text.secondary'}
                      >
                        {formatMoney(customer.outstandingBalance)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      {customer.averageRating ? customer.averageRating.toFixed(1) : '—'}
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="History - notes and old ECEEBILL bills">
                        <IconButton
                          size="small"
                          aria-label={`History for ${customer.name}`}
                          onClick={() => setHistoryFor({ id: customer.id, name: customer.name })}
                        >
                          <HistoryIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {canWrite('customer') ? (
                        <>
                          <Tooltip title="Edit">
                            <IconButton
                              size="small"
                              aria-label={`Edit ${customer.name}`}
                              onClick={() => setEditing(customer.id)}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Let them see their own file online">
                            <IconButton
                              size="small"
                              aria-label={`Portal access for ${customer.name}`}
                              onClick={() => setPortalFor({ id: customer.id, name: customer.name })}
                            >
                              <KeyIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip
                            title={
                              customer.enabled
                                ? 'Disable — keeps the order history, but off every picker a new quotation or order starts from'
                                : 'Enable — back on the pickers for new business'
                            }
                          >
                            <IconButton
                              size="small"
                              aria-label={`${customer.enabled ? 'Disable' : 'Enable'} ${customer.name}`}
                              disabled={setEnabled.isPending}
                              onClick={() =>
                                setEnabled.mutate({ id: customer.id, enabled: !customer.enabled })
                              }
                            >
                              {customer.enabled ? (
                                <BlockIcon fontSize="small" />
                              ) : (
                                <CheckCircleOutlineIcon fontSize="small" />
                              )}
                            </IconButton>
                          </Tooltip>
                        </>
                      ) : null}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {data && (
              <TablePagination
                component="div"
                count={data.totalElements}
                page={data.page}
                onPageChange={(_event, newPage) => setPage(newPage)}
                rowsPerPage={size}
                onRowsPerPageChange={(event) => {
                  setSize(Number(event.target.value))
                  setPage(0)
                }}
                rowsPerPageOptions={[10, 20, 50, 100]}
              />
            )}
          </>
        )}
      </Card>

      <NewCustomerDialog open={dialogOpen} onClose={() => setDialogOpen(false)} />
      <EditCustomerDialog customerId={editing} onClose={() => setEditing(null)} />
      <PortalAccessDialog
        customerId={portalFor?.id ?? null}
        customerName={portalFor?.name}
        onClose={() => setPortalFor(null)}
      />
      <CustomerHistoryDialog
        customerId={historyFor?.id ?? null}
        customerName={historyFor?.name}
        onClose={() => setHistoryFor(null)}
      />

    </>
  )
}

/**
 * How a customer is addressed. M/s. is the one that matters here: a firm booking
 * a hall is not a person and should not be greeted as one.
 */
const BLANK_CUSTOMER = {
  title: '',
  name: '',
  customerType: 'INDIVIDUAL',
  companyName: '',
  mobile: '',
  email: '',
  gstin: '',
  billingAddressLine1: '',
  billingAddressLine2: '',
  billingCity: '',
  billingState: '',
  billingPostalCode: '',
  foodPreference: '',
}

const TITLES = ['Mr.', 'Ms.', 'M/s.', 'Dr.'] as const

/** What a person or a firm is called before anybody types anything. */
const DEFAULT_TITLE: Record<string, string> = {
  INDIVIDUAL: 'Mr.',
  COMPANY: 'M/s.',
}

/**
 * Groups a mobile number five and five as it is typed. Anything already carrying
 * a country code is left to the person typing it - the grouping is for the ten
 * digit number the office actually deals in.
 */
function groupMobile(value: string) {
  const trimmed = value.trim()
  // A country code is kept in front and the ten digits behind it are still
  // grouped: an existing customer stored as +919840000001 should open in the
  // form reading the way the rest of the app shows it.
  const country = trimmed.startsWith('+') ? trimmed.slice(0, 3) : ''
  const digits = (country ? trimmed.slice(country.length) : trimmed).replace(/\D/g, '').slice(0, 10)
  const grouped = digits.length > 5 ? `${digits.slice(0, 5)} ${digits.slice(5)}` : digits
  return country ? `${country} ${grouped}`.trimEnd() : grouped
}

/**
 * Somebody new, entered where they are needed.
 *
 * <p>Exported because a customer is most often new at the moment they are
 * first wanted - an enquiry rung in, a quotation being written, an order taken
 * at the counter - and sending the office to the customers screen to add them
 * loses whatever was half-typed on the form they were on.</p>
 */
export function NewCustomerDialog({
  open,
  customer,
  onCreated,
  onClose,
}: {
  open: boolean
  /** Absent when adding someone new. */
  customer?: CustomerDetail | null
  /** Handed the saved customer, for a caller that opened this to pick one. */
  onCreated?: (customer: CustomerDetail) => void
  onClose: () => void
}) {
  const save = useSaveCustomer()
  const gstinLookup = useGstinLookup()
  const [form, setForm] = useState(BLANK_CUSTOMER)
  // A firm is billed on its GSTIN; a person has none to give.
  const needsGstin = form.customerType === 'COMPANY'
  const [acceptDuplicate, setAcceptDuplicate] = useState(false)
  // Looked up only once a GSTIN somebody actually typed here reaches fifteen
  // characters - not the one an existing customer already opened with, which
  // would spend a paid lookup on every edit for nothing worth filling in.
  const [gstinTouched, setGstinTouched] = useState(false)
  const [gstinNotice, setGstinNotice] = useState<string | null>(null)

  // Opens on what the customer already is, so an edit corrects rather than
  // retypes. Reset each time it opens, or yesterday's edit would leak in.
  useEffect(() => {
    if (!open) return
    setAcceptDuplicate(false)
    setGstinTouched(false)
    setGstinNotice(null)
    setForm(
      customer
        ? {
            title: customer.title ?? '',
            name: customer.name,
            customerType: customer.customerType,
            companyName: customer.companyName ?? '',
            mobile: groupMobile(customer.mobile ?? ''),
            email: customer.email ?? '',
            gstin: customer.gstin ?? '',
            billingAddressLine1: customer.billingAddressLine1 ?? '',
            billingAddressLine2: customer.billingAddressLine2 ?? '',
            billingCity: customer.billingCity ?? '',
            billingState: customer.billingState ?? '',
            billingPostalCode: customer.billingPostalCode ?? '',
            foodPreference: customer.foodPreference ?? '',
          }
        : BLANK_CUSTOMER,
    )
  }, [open, customer])

  function update<K extends keyof typeof form>(key: K, value: (typeof form)[K]) {
    setForm((current) => ({ ...current, [key]: value }))
  }

  // Filled in automatically once the GSTIN just typed is a complete one.
  // Only the fields still blank are taken - an address already typed, or
  // carried over from an existing customer, is never overwritten.
  useEffect(() => {
    const value = form.gstin.trim().toUpperCase()
    if (!gstinTouched || value.length !== 15) {
      return
    }
    let cancelled = false
    gstinLookup.mutateAsync(value).then((details) => {
      if (cancelled) return
      if (!details.found) {
        setGstinNotice('No match for this GSTIN on the GST network — enter the details by hand')
        return
      }
      const name = details.legalName ?? details.tradeName ?? null
      setGstinNotice(name ? `Filled in from the GST network: ${name}` : 'Filled in from the GST network')
      setForm((current) => ({
        ...current,
        companyName: current.companyName || name || current.companyName,
        billingAddressLine1: current.billingAddressLine1 || details.addressLine1 || current.billingAddressLine1,
        billingAddressLine2: current.billingAddressLine2 || details.addressLine2 || current.billingAddressLine2,
        billingCity: current.billingCity || details.city || current.billingCity,
        billingState: current.billingState || details.state || current.billingState,
        billingPostalCode: current.billingPostalCode || details.pincode || current.billingPostalCode,
      }))
    }).catch(() => {
      if (!cancelled) setGstinNotice('Could not look that GSTIN up just now — enter the details by hand')
    })
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.gstin, gstinTouched])

  const duplicateWarning =
    save.error instanceof ApiError && save.error.code === 'POSSIBLE_DUPLICATE_CUSTOMER'

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>New customer</DialogTitle>
      <DialogContent dividers>
        <ErrorBanner error={save.error} />
        {duplicateWarning && !acceptDuplicate && (
          <Button size="small" sx={{ mb: 2 }} onClick={() => setAcceptDuplicate(true)}>
            Save anyway — this is a different customer
          </Button>
        )}
        <Grid container spacing={2} sx={{ mt: 0 }}>
          <Grid item xs={12} md={4}>
            <TextField
              select
              label="Customer type"
              value={form.customerType}
              onChange={(event) => {
                const chosen = event.target.value
                setForm((current) => ({
                  ...current,
                  customerType: chosen,
                  // The title follows the type unless somebody has already
                  // said otherwise: a firm is M/s. and a person is Mr. until
                  // told which person.
                  title: DEFAULT_TITLE[chosen] ?? current.title,
                }))
              }}
              fullWidth
              helperText="A firm is billed as M/s. and needs a GSTIN"
            >
              {CUSTOMER_TYPES.map((value) => (
                <MenuItem key={value} value={value}>
                  {humanise(value)}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={4} md={2}>
            {/* Asked for, not offered: a customer is written to and spoken to,
                and a record without a title leaves whoever is writing to
                guess. */}
            <TextField
              select
              required
              label="Title"
              value={form.title}
              onChange={(event) => update('title', event.target.value)}
              fullWidth
              error={!form.title}
              helperText={form.title ? undefined : 'Choose one'}
            >
              {TITLES.map((value) => (
                <MenuItem key={value} value={value}>
                  {value}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={8} md={4}>
            <TextField
              label="Name"
              value={form.name}
              onChange={(event) => update('name', event.target.value)}
              fullWidth
              required
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Company name"
              value={form.companyName}
              onChange={(event) => update('companyName', event.target.value)}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              label="Mobile"
              value={form.mobile}
              // Grouped five and five as it is typed, which is how the number is
              // read back over the phone and checked against a screen.
              onChange={(event) => update('mobile', groupMobile(event.target.value))}
              fullWidth
              required
              placeholder="98400 12345"
              inputProps={{ inputMode: 'numeric', maxLength: 15 }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              label="Email"
              value={form.email}
              onChange={(event) => update('email', event.target.value)}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={4}>
            {/* A firm is billed on its GSTIN - without one the invoice cannot
                claim to be a B2B supply, and the customer cannot claim the
                credit. An individual has none, so it is not demanded of them. */}
            <TextField
              label="GSTIN"
              value={form.gstin}
              onChange={(event) => {
                setGstinTouched(true)
                setGstinNotice(null)
                update('gstin', event.target.value.toUpperCase())
              }}
              fullWidth
              required={needsGstin}
              error={needsGstin && form.gstin.trim().length === 0}
              helperText={
                gstinNotice
                  ?? (needsGstin ? 'Required for a company; the invoice is raised against it' : undefined)
              }
              inputProps={{ maxLength: 15 }}
              InputProps={{
                endAdornment: gstinLookup.isPending ? (
                  <InputAdornment position="end">
                    <CircularProgress size={18} />
                  </InputAdornment>
                ) : undefined,
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Address"
              value={form.billingAddressLine1}
              onChange={(event) => update('billingAddressLine1', event.target.value)}
              fullWidth
              placeholder="Door and street"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Address line 2"
              value={form.billingAddressLine2}
              onChange={(event) => update('billingAddressLine2', event.target.value)}
              fullWidth
              placeholder="Area or landmark"
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              label="City"
              value={form.billingCity}
              onChange={(event) => update('billingCity', event.target.value)}
              fullWidth
            />
          </Grid>
          <Grid item xs={6} md={4}>
            <TextField
              label="State"
              value={form.billingState}
              onChange={(event) => update('billingState', event.target.value)}
              fullWidth
            />
          </Grid>
          <Grid item xs={6} md={4}>
            <TextField
              label="Pincode"
              value={form.billingPostalCode}
              onChange={(event) =>
                update('billingPostalCode', event.target.value.replace(/\D/g, '').slice(0, 6))
              }
              fullWidth
              placeholder="600116"
              inputProps={{ inputMode: 'numeric', maxLength: 6 }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              select
              label="Food preference"
              value={form.foodPreference}
              onChange={(event) => update('foodPreference', event.target.value)}
              fullWidth
            >
              <MenuItem value="">Not recorded</MenuItem>
              {['VEG', 'JAIN'].map((value) => (
                <MenuItem key={value} value={value}>
                  {humanise(value)}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={
            save.isPending || !form.title || !form.name || !form.mobile
            || (needsGstin && form.gstin.trim().length === 0)
          }
          onClick={async () => {
            const saved = await save.mutateAsync({
              id: customer?.id,
              body: {
                ...form,
                title: form.title || undefined,
                companyName: form.companyName || undefined,
                billingAddressLine1: form.billingAddressLine1 || undefined,
                billingAddressLine2: form.billingAddressLine2 || undefined,
                billingState: form.billingState || undefined,
                billingPostalCode: form.billingPostalCode || undefined,
                email: form.email || undefined,
                gstin: form.gstin || undefined,
                foodPreference: form.foodPreference || undefined,
                acceptDuplicateWarning: acceptDuplicate,
              },
            })
            onCreated?.(saved)
            onClose()
          }}
        >
          {customer ? 'Save changes' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

/**
 * Opens the customer form on someone who already exists.
 *
 * <p>The list row carries a summary; the address, GSTIN and preferences it does
 * not hold are read first, so an edit corrects the whole record rather than
 * blanking the parts the list never knew about.</p>
 */
function EditCustomerDialog({
  customerId,
  onClose,
}: {
  customerId: string | null
  onClose: () => void
}) {
  const { data } = useCustomer(customerId ?? undefined)
  if (!customerId) return null
  return <NewCustomerDialog open={Boolean(data)} customer={data} onClose={onClose} />
}
